import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import emailjs from "@emailjs/browser";
import { dishes } from "../data/dishes";
import { business } from "../data/business";
import { useTheme, useDocumentTitle } from "../hooks/useSite";
import { Host } from "../illustrations";

// Existing EmailJS booking integration (same service, template and field
// names as the previous booking form). A request is sent; a booking is
// confirmed only when the café contacts the guest.
const EMAILJS = { service: "service_zleknzbbn", template: "template_7xxueuh", key: "JiNk4fjq66o6LTiKN" };
const sources = ["Instagram", "Facebook", "Bill desk", "Youtube", "Others"];
const modes = ["In-store Event", "Outdoor Event"];
const types = ["Birthday Event", "Get together Event", "Marriage Event", "Anniversary Event", "Kitty party", "Others"];

function Field({ label, error, children }) {
  return (
    <label className={`ro-field ${error ? "is-invalid" : ""}`}>
      <span className="ro-field__label">{label}</span>
      {children}
      {error && <span className="ro-field__error">{error.message}</span>}
    </label>
  );
}

export default function BookEventPage() {
  useTheme(dishes[0]);
  useDocumentTitle("Book an event");
  const [params] = useSearchParams();
  const [status, setStatus] = useState({ state: "idle" });
  const presetMode = params.get("mode") || "";
  const presetOccasion = params.get("occasion") || "";
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: { mode: modes.includes(presetMode) ? presetMode : "", message: presetOccasion ? `Occasion: ${presetOccasion}. ` : "" } });

  const required = { required: "This field is required." };

  const send = async (data) => {
    setStatus({ state: "sending" });
    try {
      await emailjs.send(EMAILJS.service, EMAILJS.template, data, EMAILJS.key);
      setStatus({ state: "sent" });
      reset();
    } catch (e) {
      setStatus({ state: "error" });
    }
  };

  return (
    <>
      <section className="ro-education-intro ro-form-intro">
        <div className="ro-container ro-split">
          <div>
            <p className="ro-item-crumb">
              <Link to="/events">Celebrations</Link> / Book an event
            </p>
            <h1>
              <strong className="ro-italic">Host your party</strong>
              <br />
              <span className="ro-outline">at RollOver.</span>
            </h1>
            <p className="ro-education-intro__sub">Amaze your guests.</p>
          </div>
          <Host className="ro-form-intro__figure" />
        </div>
      </section>
      <section className="ro-section">
        <div className="ro-container ro-container--narrow">
          {status.state === "sent" ? (
            <div className="ro-form-result" role="status">
              <h2>
                Request <span className="ro-outline">received.</span>
              </h2>
              <p>Your booking request has been emailed to the RollOver team. It is not a confirmed booking yet; the team will contact you by phone or email.</p>
              <div className="ro-availability__actions" style={{ justifyContent: "flex-start" }}>
                <button type="button" className="ro-pill ro-pill--solid" onClick={() => setStatus({ state: "idle" })}>
                  Send another request
                </button>
                <Link className="ro-pill" to="/events">
                  Back to celebrations
                </Link>
              </div>
            </div>
          ) : (
            <form className="ro-form" onSubmit={handleSubmit(send)} noValidate>
              <div className="ro-form__grid">
                <Field label="Full name" error={errors.name}>
                  <input type="text" autoComplete="name" {...register("name", required)} />
                </Field>
                <Field label="Phone number" error={errors.number}>
                  <input type="tel" autoComplete="tel" {...register("number", required)} />
                </Field>
                <Field label="E-mail" error={errors.email}>
                  <input type="email" autoComplete="email" {...register("email", { ...required, pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Invalid e-mail." } })} />
                </Field>
                <Field label="Address" error={errors.location}>
                  <input type="text" autoComplete="street-address" {...register("location", required)} />
                </Field>
                <Field label="Event date" error={errors.date}>
                  <input type="date" {...register("date", required)} />
                </Field>
                <Field label="Event time" error={errors.time}>
                  <input type="time" {...register("time", required)} />
                </Field>
                <Field label="How did you find us?" error={errors.why}>
                  <select {...register("why", required)} defaultValue="">
                    <option value="" disabled>
                      Choose one
                    </option>
                    {sources.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Event mode" error={errors.mode}>
                  <select {...register("mode", required)}>
                    <option value="" disabled>
                      Choose one
                    </option>
                    {modes.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Event type" error={errors.type}>
                  <select {...register("type", required)} defaultValue="">
                    <option value="" disabled>
                      Choose one
                    </option>
                    {types.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Guest count" error={errors.count}>
                  <input type="number" min="1" {...register("count", required)} />
                </Field>
              </div>
              <Field label="Your message" error={errors.message}>
                <textarea rows="5" {...register("message", { ...required, minLength: { value: 10, message: "No less than 10 characters." } })} />
              </Field>
              <div className="ro-form__actions">
                <button type="submit" className="ro-pill ro-pill--solid" disabled={isSubmitting || status.state === "sending"}>
                  {status.state === "sending" ? "Sending…" : "Book event"}
                </button>
                <p className="ro-small" role="status" aria-live="polite">
                  {status.state === "error" ? "The request could not be sent. Please try again or call " + business.phone.display + "." : "Sends an email request to the café. A booking is confirmed only when the team contacts you."}
                </p>
              </div>
            </form>
          )}
        </div>
      </section>
    </>
  );
}
